/**
 * Wire status codes are never displayed raw — each maps to an i18n label and
 * a badge variant. The dot uses logical `me-*` spacing so it mirrors in RTL.
 */
import { useTranslation } from "react-i18next";
import { Badge, cn } from "@erp/ui";

import { enumLabel } from "../labels";

import type { LandedCostStatus, PurchaseOrderStatus, TransmissionStatus } from "../api";

type BadgeVariant = "default" | "secondary" | "outline" | "destructive" | "success";

const PO_VARIANTS: Record<PurchaseOrderStatus, BadgeVariant> = {
  draft: "outline",
  pending_approval: "secondary",
  approved: "default",
  sent: "default",
  partially_received: "secondary",
  received: "success",
  closed: "outline",
};

const PO_DOTS: Record<PurchaseOrderStatus, string> = {
  draft: "bg-muted-foreground",
  pending_approval: "bg-amber-500",
  approved: "bg-sky-500",
  sent: "bg-indigo-500",
  partially_received: "bg-amber-500",
  received: "bg-emerald-500",
  closed: "bg-muted-foreground",
};

export function PurchaseOrderStatusBadge({ status }: { status: PurchaseOrderStatus }) {
  const { t } = useTranslation();
  return (
    <Badge variant={PO_VARIANTS[status]} className="whitespace-nowrap">
      <span
        aria-hidden="true"
        className={cn("me-1.5 inline-block size-1.5 shrink-0 rounded-full", PO_DOTS[status])}
      />
      {enumLabel(t, "poStatus", status)}
    </Badge>
  );
}

const LANDED_COST_VARIANTS: Record<LandedCostStatus, BadgeVariant> = {
  draft: "outline",
  posted: "success",
};

export function LandedCostStatusBadge({ status }: { status: LandedCostStatus }) {
  const { t } = useTranslation();
  return (
    <Badge variant={LANDED_COST_VARIANTS[status]}>{enumLabel(t, "landedCostStatus", status)}</Badge>
  );
}

const TRANSMISSION_VARIANTS: Record<TransmissionStatus, BadgeVariant> = {
  queued: "secondary",
  sent: "success",
  failed: "destructive",
};

export function TransmissionStatusBadge({ status }: { status: TransmissionStatus }) {
  const { t } = useTranslation();
  return (
    <Badge variant={TRANSMISSION_VARIANTS[status]}>
      {enumLabel(t, "transmissionStatus", status)}
    </Badge>
  );
}
